import { useMutation, useQuery } from '@apollo/client';
import { Button, Grid, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  getProperties,
  createProperty as createPropertyMutation,
  Property,
  PermissionsEnum,
  MIXPANEL_EVENTS,
  Configuration,
  CONFIGURATION_TYPE,
} from 'vendor-marketplace-shared-library';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useMemo, useState } from 'react';
import PropertyFormModal from '../../components/property/PropertyFormModal';
import { PropertyFormInput } from '../../types/property';
import useAuth from '../../hooks/useAuth';
import useMixpanel from '../../hooks/useMixpanel';
import useConfigurations from '../../hooks/useConfigurations';
import { getConfigurationValue } from '../../utilities/helpers';

export function PropertiesPage() {
  const { validateUserPermissions } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { configurationsQuery } = useConfigurations();
  const { mixpanelTrack } = useMixpanel();
  const propertyId = params['id'];
  const [openCreatePropertyModal, setOpenCreatePropertyModal] = useState<boolean>(false);
  const { data: propertiesData } = useQuery(getProperties);
  const properties = propertiesData?.getProperties || [];

  const userWarrantyProductIsEnabledConfiguration =
    configurationsQuery?.data?.getConfigurations?.find(
      (configuration: Configuration) =>
        configuration.name === CONFIGURATION_TYPE.WARRANTY_PRODUCT_IS_ENABLED,
    );

  const isUserWarrantyProductEnabled = getConfigurationValue(
    userWarrantyProductIsEnabledConfiguration,
  );

  const columnWidth = (window.innerWidth * 0.75) / (isUserWarrantyProductEnabled ? 2 : 3);
  const columnWidthOnMobile = 175;
  const calculatedColumnWidth = window.innerWidth > 600 ? columnWidth : columnWidthOnMobile;

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: calculatedColumnWidth },
    { field: 'fullAddress', headerName: 'Address', width: calculatedColumnWidth * 1.2 },
    {
      field: 'tenantWorkOrderFormEnabled',
      headerName: 'Tenant Work Order Form Enabled',
      width: calculatedColumnWidth,
    },
  ];

  const filteredColumns = columns.filter(column => {
    if (isUserWarrantyProductEnabled) {
      return column.field !== 'tenantWorkOrderFormEnabled';
    } else {
      return column;
    }
  });

  const rows = useMemo(() => {
    return properties.map((property: Property) => ({
      ...property,
      id: property.id,
      tenantWorkOrderFormEnabled: property.tenantWorkOrderForm?.enabled ? 'Yes' : 'No',
    }));
  }, [properties]);

  const [createProperty] = useMutation(createPropertyMutation, {
    refetchQueries: [getProperties],
    onCompleted: () => {
      setOpenCreatePropertyModal(false);
    },
  });

  const onCreate = async (formData: PropertyFormInput) => {
    await createProperty({
      variables: {
        id: propertyId,
        input: formData,
      },
    });
  };

  return (
    <Grid container spacing={4}>
      <Grid item container sx={styles.subContainer} rowGap={2}>
        <Grid item>
          <Typography variant={'h5'}>Properties in your organization</Typography>
        </Grid>
        {validateUserPermissions([PermissionsEnum.PROPERTIES_CREATE]) && (
          <Grid item>
            <Button
              onClick={() => {
                setOpenCreatePropertyModal(true);
                mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_CREATE_PROPERTY_PRESSED);
              }}
            >
              Add a property
            </Button>
          </Grid>
        )}
      </Grid>
      <Grid item sx={styles.tableContainer}>
        <Grid container sx={styles.nowrap}>
          <Grid item xs={12}>
            <DataGrid
              sx={styles.dataGrid}
              rows={rows}
              columns={filteredColumns}
              disableColumnMenu
              onRowClick={row => {
                navigate(`/properties/${row.row.id}`);
                mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_PROPERTY_ROW_PRESSED, {
                  propertyId: row.row.id,
                });
              }}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[5, 10]}
            />
          </Grid>
        </Grid>
      </Grid>
      {openCreatePropertyModal && (
        <PropertyFormModal
          open={openCreatePropertyModal}
          setOpen={setOpenCreatePropertyModal}
          onSubmit={onCreate}
        />
      )}
    </Grid>
  );
}

const styles = {
  subContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dataGrid: {
    minWidth: '525px',
    minHeight: 150,
    '& .MuiDataGrid-cell:focus': {
      outline: ' none',
    },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    // hide the scrollbars
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'hidden !important',
    },
    '& .MuiDataGrid-scrollbar--horizontal': {
      display: 'none',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    maxWidth: { xs: '310px', sm: '100%' },
  },
  nowrap: {
    flexWrap: 'nowrap',
  },
};
